// /settings/notifications and /settings/notifications/groups/:groupId.
//
// The type-by-channel grid from docs/plan-notifications.md (step 4): "Your
// defaults" on the first screen, then one row per group that either uses those
// defaults or is customized, opening the same grid for that group with a Reset.
//
// Three things the copy has to be honest about, because each is invisible
// otherwise:
//   • Push is PER DEVICE. Turning it on here does nothing for your phone.
//   • A switch is a CHOICE, stored even when its channel cannot reach you yet
//     (no verified phone, no device). The column says so rather than hiding it.
//   • Unsubscribing kills notification email everywhere, but never the account
//     mail that recovers a password.

import {
  ApiClientError,
  fetchGroupNotificationSettings,
  fetchNotificationSettings,
  sendPushTest,
  resetGroupNotificationSettings,
  resetNotificationDefaults,
  resubscribeOwnEmail,
  updateGroupNotificationSettings,
  updateNotificationDefaults,
} from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import {
  NOTIFICATION_PRESET_IDS,
  NOTIFICATION_PRESETS,
  NOTIF_CHANNELS,
  NOTIF_TYPES,
  NOTIF_TYPE_LABELS,
  matchingPreset,
  presetCells,
  type NotificationPresetId,
  type NotifCellChange,
  type NotifChannel,
  type NotifGrid,
  type NotifReach,
  type NotificationSettingsResponse,
} from '@msc/shared';
import { disablePush, enablePush, pushState } from '../push.js';
import { isNative } from '../platform.js';
import { iosInstallHelp, isIosBrowserTab } from './installHelp.js';

const CHANNEL_LABEL: Record<NotifChannel, string> = { email: 'Email', sms: 'Text', push: 'Push' };

// --- /settings/notifications ----------------------------------------------

export const notificationsView: View = async (outlet) => {
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  let data: NotificationSettingsResponse;
  try {
    data = await fetchNotificationSettings();
  } catch (err) {
    if (loginRedirect(err)) return;
    main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load your notification settings.'));
    return;
  }

  render(data);

  function render(current: NotificationSettingsResponse): void {
    main.replaceChildren(
      el('h1', {}, 'Notifications'),
      el(
        'p',
        { class: 'muted small' },
        'Choose how each kind of notification reaches you. Every group uses these ' +
          'defaults unless you customize it below.',
      ),
      stylesCard(current, render),
      pushCard(current),
      ...(current.emailUnsubscribed ? [unsubscribedNotice(render)] : []),
      defaultsCard(current, render),
      groupsCard(current),
    );
  }
};

/**
 * "Pick a style": four named bundles of the member's own defaults (shared
 * `notifyPresets.ts`). Applying one sends its cells through the same PATCH the grid
 * below uses, so nothing here is a new write path. Which style is current is DERIVED
 * from the grid, so editing a switch below honestly turns it into "Custom".
 */
function stylesCard(
  data: NotificationSettingsResponse,
  onChanged: (next: NotificationSettingsResponse) => void,
): HTMLElement {
  const current = matchingPreset(data.defaults);
  const status = el('p', { class: 'muted small', role: 'status' }, '');
  const grid = el('div', { class: 'style-grid' });

  for (const id of NOTIFICATION_PRESET_IDS) {
    const preset = NOTIFICATION_PRESETS[id];
    const noPhone = preset.needsPhone && data.reach.sms !== null;
    const isCurrent = current === id;
    const option = el(
      'button',
      {
        type: 'button',
        class: 'style-option',
        'aria-pressed': isCurrent ? 'true' : 'false',
        disabled: noPhone,
      },
      [
        el('span', { class: 'style-name' }, [
          preset.label,
          isCurrent ? el('span', { class: 'style-current' }, 'Current') : '',
        ]),
        el('span', { class: 'muted small' }, preset.blurb),
        noPhone
          ? el('span', { class: 'muted small' }, 'Needs a verified phone number. Add one on your account page.')
          : '',
      ],
    );
    option.addEventListener('click', () => void apply(id, option));
    grid.append(option);
  }

  async function apply(id: NotificationPresetId, option: HTMLElement): Promise<void> {
    const preset = NOTIFICATION_PRESETS[id];
    const ok = window.confirm(
      `Switch to "${preset.label}"? This replaces your own defaults below. Groups you ` +
        'customized keep their own settings, and you can still change any switch afterwards.',
    );
    if (!ok) return;
    option.setAttribute('disabled', 'disabled');
    status.textContent = 'Saving';
    try {
      const next = await updateNotificationDefaults({ cells: presetCells(id) });
      onChanged(next);
    } catch (err) {
      status.textContent = errorText(err);
      option.removeAttribute('disabled');
    }
  }

  const deviceNote =
    current === 'phone' || current === 'computer'
      ? el(
          'p',
          { class: 'muted small' },
          isNative()
            ? 'Push notifications are not available in the app yet. Until they are, this style ' +
                'reaches you by email.'
            : data.reach.push === 'noDevice'
              ? 'Push is per device, and none is turned on yet. Use "Push on this device" below ' +
                'on the phone or computer you want it on.'
              : 'Push is per device. Turn it on for each phone or computer you want it on, under "Push on this device" below.',
        )
      : '';

  return el('section', { class: 'card' }, [
    el('h2', {}, 'Pick a style'),
    el(
      'p',
      { class: 'muted small' },
      current === null
        ? 'A quick way to set all of your defaults at once. Yours are custom right now.'
        : 'A quick way to set all of your defaults at once. You can still change any switch below.',
    ),
    grid,
    deviceNote,
    status,
  ]);
}

function defaultsCard(
  data: NotificationSettingsResponse,
  onChanged: (next: NotificationSettingsResponse) => void,
): HTMLElement {
  const status = el('p', { class: 'muted small', role: 'status' }, '');
  const reset = el('button', { type: 'button', class: 'btn' }, 'Reset to standard defaults');
  const actions = el('div', { class: 'notif-actions', hidden: !anyOwn(data.defaults) }, reset);
  const grid = gridTable(data.defaults, data.reach, 'own', status, async (cells) => {
    const next = await updateNotificationDefaults({ cells });
    actions.hidden = !anyOwn(next.defaults);
    return next.defaults;
  });

  reset.addEventListener('click', async () => {
    const ok = window.confirm(
      'Reset your defaults to the standard ones? Any text messages you turned on will be ' +
        'turned off. Groups you customized keep their own settings.',
    );
    if (!ok) return;
    reset.setAttribute('disabled', 'disabled');
    status.textContent = 'Saving';
    try {
      onChanged(await resetNotificationDefaults());
    } catch (err) {
      status.textContent = errorText(err);
      reset.removeAttribute('disabled');
    }
  });

  return el('section', { class: 'card' }, [
    el('h2', {}, 'Your defaults'),
    reachNotes(data.reach, true),
    grid,
    status,
    actions,
  ]);
}

/** Whether the member has set any of their own defaults, i.e. Reset would change something. */
function anyOwn(grid: NotifGrid): boolean {
  return NOTIF_TYPES.some((t) => NOTIF_CHANNELS.some((c) => grid[t][c]?.source === 'own'));
}

function groupsCard(data: NotificationSettingsResponse): HTMLElement {
  if (data.groups.length === 0) {
    return el('section', { class: 'card' }, [
      el('h2', {}, 'Groups'),
      el('p', { class: 'muted' }, 'You are not in any groups yet.'),
    ]);
  }
  return el('section', { class: 'card' }, [
    el('h2', {}, 'Groups'),
    el(
      'ul',
      { class: 'notif-group-list' },
      data.groups.map((g) =>
        el('li', {}, [
          el('a', { href: `/settings/notifications/groups/${g.groupId}` }, [
            el('span', { class: 'notif-group-name' }, g.groupName),
            el(
              'span',
              { class: g.customized ? 'notif-badge is-custom' : 'notif-badge' },
              g.customized ? 'Customized' : 'Uses your defaults',
            ),
          ]),
        ]),
      ),
    ),
  ]);
}

// --- /settings/notifications/groups/:groupId ------------------------------

export const groupNotificationsView: View = async (outlet, ctx) => {
  const groupId = Number(ctx.params.groupId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(groupId) || groupId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'That group could not be found.'));
    return;
  }

  try {
    render(await fetchGroupNotificationSettings(groupId));
  } catch (err) {
    if (loginRedirect(err)) return;
    main.replaceChildren(
      el('p', { class: 'form-error' }, 'Could not load the notification settings for that group.'),
    );
  }

  function render(data: Awaited<ReturnType<typeof fetchGroupNotificationSettings>>): void {
    const status = el('p', { class: 'muted small', role: 'status' }, '');
    const reset = el('button', { type: 'button', class: 'btn' }, 'Reset to my defaults');
    const actions = el('div', { class: 'notif-actions', hidden: !data.customized }, reset);

    const grid = gridTable(data.grid, data.reach, 'override', status, async (cells) => {
      const next = await updateGroupNotificationSettings(groupId, { cells });
      actions.hidden = !next.customized;
      return next.grid;
    });

    reset.addEventListener('click', async () => {
      reset.setAttribute('disabled', 'disabled');
      status.textContent = 'Saving';
      try {
        render(await resetGroupNotificationSettings(groupId));
      } catch (err) {
        status.textContent = errorText(err);
        reset.removeAttribute('disabled');
      }
    });

    main.replaceChildren(
      el('h1', {}, data.groupName),
      el('p', { class: 'muted small' }, [
        'Notifications for this group. Anything not marked as changed here follows ',
        el('a', { href: '/settings/notifications' }, 'your defaults'),
        '.',
      ]),
      el('section', { class: 'card' }, [
        reachNotes(data.reach, false),
        grid,
        el('p', { class: 'muted small notif-legend' }, [
          el('span', { class: 'notif-dot', 'aria-hidden': 'true' }),
          ' Changed for this group',
        ]),
        status,
        actions,
      ]),
    );
  }
};

// --- The grid ---------------------------------------------------------------

/**
 * The type-by-channel table, saved per switch with no submit button.
 *
 * Updated IN PLACE from the server's answer rather than re-rendered, so focus
 * stays on the switch a keyboard user just pressed. A failed save reverts the
 * switch: a settings screen that shows a state the server refused is worse than
 * a slow one.
 *
 * `marked` is the layer whose cells get the "changed" dot: overrides on a group
 * page. On the defaults page nothing is marked; the Reset button says enough.
 */
function gridTable(
  initial: NotifGrid,
  reach: NotifReach,
  marked: 'own' | 'override',
  status: HTMLElement,
  save: (cells: NotifCellChange[]) => Promise<NotifGrid>,
): HTMLElement {
  const boxes: Array<{ type: (typeof NOTIF_TYPES)[number]; channel: NotifChannel; box: HTMLInputElement; cell: HTMLElement }> = [];

  const apply = (grid: NotifGrid): void => {
    for (const b of boxes) {
      const c = grid[b.type][b.channel];
      if (!c) continue;
      b.box.checked = c.enabled;
      b.cell.classList.toggle('is-changed', marked === 'override' && c.source === 'override');
    }
  };

  const head = el('tr', {}, [
    el('th', { scope: 'col' }, el('span', { class: 'visually-hidden' }, 'Notification')),
    ...NOTIF_CHANNELS.map((ch) =>
      el(
        'th',
        { scope: 'col', class: reach[ch] ? 'unreachable' : undefined },
        CHANNEL_LABEL[ch],
      ),
    ),
  ]);

  const body = NOTIF_TYPES.map((type) => {
    const { label, help } = NOTIF_TYPE_LABELS[type];
    const cells = NOTIF_CHANNELS.map((channel) => {
      const c = initial[type][channel];
      if (!c) {
        return el('td', { class: 'notif-na' }, [
          el('span', { 'aria-hidden': 'true' }, '–'),
          el('span', { class: 'visually-hidden' }, `${CHANNEL_LABEL[channel]} not offered`),
        ]);
      }
      const box = el('input', {
        type: 'checkbox',
        'aria-label': `${label} by ${CHANNEL_LABEL[channel].toLowerCase()}`,
      }) as HTMLInputElement;
      const cell = el('td', { class: reach[channel] ? 'unreachable' : undefined }, box);
      boxes.push({ type, channel, box, cell });

      box.addEventListener('change', async () => {
        // Decision Q4: the day-of check-in is the reason the app exists, so
        // turning off its LAST channel asks first.
        if (type === 'dayOf' && !box.checked) {
          const othersOn = boxes.some((b) => b.type === 'dayOf' && b.box !== box && b.box.checked);
          if (
            !othersOn &&
            !window.confirm(
              'Turn off the day-of check-in completely? It is the “still in?” message that ' +
                'tells the organizer who is really coming.',
            )
          ) {
            box.checked = true;
            return;
          }
        }
        box.setAttribute('disabled', 'disabled');
        status.textContent = 'Saving';
        try {
          apply(await save([{ type, channel, enabled: box.checked }]));
          status.textContent = 'Saved.';
        } catch (err) {
          box.checked = !box.checked;
          status.textContent = errorText(err);
        } finally {
          box.removeAttribute('disabled');
        }
      });
      return cell;
    });

    return el('tr', {}, [
      el('th', { scope: 'row' }, [
        el('span', { class: 'notif-type' }, label),
        el('span', { class: 'muted small notif-help' }, help),
      ]),
      ...cells,
    ]);
  });

  const table = el('table', { class: 'notif-grid' }, [el('thead', {}, head), el('tbody', {}, body)]);
  apply(initial);
  return el('div', { class: 'notif-grid-wrap' }, table);
}

/** One line per channel that cannot reach the member, saying what would fix it. */
function reachNotes(reach: NotifReach, onSettingsPage: boolean): HTMLElement {
  const notes: Array<string | HTMLElement> = [];
  if (reach.email === 'unsubscribed') {
    notes.push('Email is unsubscribed, so the email switches send nothing until you turn it back on.');
  } else if (reach.email === 'noAddress') {
    notes.push('There is no email address on your account.');
  }
  if (reach.sms) {
    notes.push(
      el('span', {}, [
        'Text messages need a verified phone number. ',
        el('a', { href: '/account' }, 'Add one on your account'),
        '.',
      ]),
    );
  }
  if (reach.push) {
    notes.push(
      onSettingsPage
        ? 'Push needs a device. Turn it on for this browser above.'
        : el('span', {}, [
            'Push needs a device. ',
            el('a', { href: '/settings/notifications' }, 'Turn it on for this browser'),
            '.',
          ]),
    );
  }
  if (notes.length === 0) return el('div', { hidden: true });
  return el(
    'ul',
    { class: 'notif-reach muted small' },
    notes.map((n) => el('li', {}, n)),
  );
}

// --- This device ------------------------------------------------------------

function pushCard(data: NotificationSettingsResponse): HTMLElement {
  const status = el('p', { class: 'muted small' }, 'Checking this device');
  const actions = el('div', {});
  const card = el('section', { class: 'card' }, [
    el('h2', {}, 'Push on this device'),
    el(
      'p',
      { class: 'muted small' },
      isNative()
        ? 'Event notifications reach you by email and text message.'
        : 'Push is per device. Turning it on here covers this browser only. Do it again ' +
          'on your phone if you want it there.',
    ),
    status,
    actions,
  ]);

  // Native push has no sender yet (needs the APNs key), and PushManager does not
  // exist in a WKWebView, so the browser flow below would show browser-flavoured
  // copy inside the app. See docs/plan-ios-ship.md, item 4.
  if (isNative()) {
    status.textContent =
      'Push notifications are not available in the app yet. You will still get event ' +
      'notifications by email and text message.';
    return card;
  }

  if (!data.vapidPublicKey) {
    status.textContent = 'Push notifications are not enabled on this server.';
    return card;
  }

  void render();

  async function render(): Promise<void> {
    const state = await pushState();
    actions.replaceChildren();

    if (!state.supported) {
      // On an iPhone in a Safari tab the honest answer is "add it to the Home Screen",
      // not "unsupported".
      if (isIosBrowserTab()) {
        status.textContent = '';
        actions.append(iosInstallHelp());
        return;
      }
      status.textContent = state.reason;
      return;
    }
    // ⚠ A subscription is NOT permission. The browser can keep a subscription
    // while the site's notification permission is Ask or Block (changed in site
    // settings, or never granted on this profile), and then every push arrives and
    // is silently refused: DevTools says "No notification permission has been
    // granted for this origin". Found 2026-10-20 when this page said "On" for a
    // browser that showed nothing. Say so, and offer the retry.
    if (state.subscribed && state.permission !== 'granted') {
      status.textContent =
        'This browser has notifications turned off for this site, so alerts will not show even ' +
        'though a device is registered. Click the lock icon in the address bar, set ' +
        'Notifications to Allow, then reload this page.';
      actions.append(
        button('Ask the browser again', async () => enablePush(data.vapidPublicKey), render),
      );
      return;
    }
    if (state.subscribed) {
      status.textContent =
        data.pushDevices > 1
          ? `On for this device (${data.pushDevices} devices total).`
          : 'On for this device.';
      actions.append(
        button('Turn off for this device', async () => disablePush(), render),
        ' ',
        testButton(),
      );
      return;
    }
    if (state.permission === 'denied') {
      status.textContent =
        'Blocked for this site. Allow notifications in your browser settings, then reload.';
      return;
    }
    status.textContent = 'Off for this device.';
    actions.append(
      button('Turn on for this device', async () => enablePush(data.vapidPublicKey), render),
    );
  }

  return card;
}

/**
 * "Send me a test notification": the self-service version of what used to take a
 * developer console. The server answers how many devices the push service accepted;
 * whether the operating system then SHOWS it is outside our reach, so the result
 * names the usual suspects instead of claiming success.
 */
function testButton(): HTMLElement {
  const out = el('p', { class: 'muted small', role: 'status' });
  const btn = el('button', { class: 'btn btn-secondary', type: 'button' }, 'Send me a test notification');
  btn.addEventListener('click', async () => {
    btn.setAttribute('disabled', 'disabled');
    out.textContent = 'Sending';
    try {
      const r = await sendPushTest();
      out.textContent =
        r.devices === 0
          ? 'No device is registered yet. Turn push on for this device first.'
          : r.sent === 0
            ? 'The push service did not accept it. Turn push off and on again for this device.'
            : 'Sent. It should appear within a few seconds. If it does not, check that your ' +
              'computer or phone has notifications on and Do Not Disturb off (on Windows: ' +
              'Settings, System, Notifications), and that this site is allowed in the browser.';
    } catch {
      out.textContent = 'Could not send the test. Try again.';
    } finally {
      btn.removeAttribute('disabled');
    }
  });
  return el('span', {}, [btn, out]);
}

/**
 * A button whose action returns an error message or null.
 *
 * `enablePush` must run inside the click handler: browsers require the
 * permission prompt to come from a user gesture, and an intervening await
 * loses it.
 */
function button(
  label: string,
  action: () => Promise<string | null>,
  onDone: () => Promise<void>,
): HTMLElement {
  const error = el('p', { class: 'form-error', hidden: true });
  const btn = el('button', { class: 'btn btn-standalone', type: 'button' }, label);

  btn.addEventListener('click', async () => {
    error.hidden = true;
    btn.setAttribute('disabled', 'disabled');
    const message = await action();
    if (message) {
      error.textContent = message;
      error.hidden = false;
      btn.removeAttribute('disabled');
      return;
    }
    await onDone();
  });

  return el('div', {}, [btn, error]);
}

function unsubscribedNotice(onChanged: (next: NotificationSettingsResponse) => void): HTMLElement {
  const status = el('p', { class: 'muted small' }, '');
  const btn = el('button', { type: 'button', class: 'btn' }, 'Turn email back on');

  btn.addEventListener('click', async () => {
    btn.setAttribute('disabled', 'disabled');
    status.textContent = 'Saving';
    try {
      await resubscribeOwnEmail();
      onChanged(await fetchNotificationSettings());
    } catch (err) {
      status.textContent = errorText(err);
      btn.removeAttribute('disabled');
    }
  });

  return el('section', { class: 'card' }, [
    el('h2', {}, 'Email is unsubscribed'),
    el(
      'p',
      {},
      'You unsubscribed from notification email, so the email switches below send ' +
        'nothing. Account email, like password resets and verification, always still sends.',
    ),
    btn,
    status,
  ]);
}

// --- Helpers ----------------------------------------------------------------

function loginRedirect(err: unknown): boolean {
  if (err instanceof ApiClientError && err.status === 401) {
    navigate('/login', true);
    return true;
  }
  return false;
}

function errorText(err: unknown): string {
  return err instanceof ApiClientError ? err.message : 'Could not save that change.';
}
